import { PageContainer } from '@ant-design/pro-components'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Alert, Button, Descriptions, Skeleton, Space, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { ArrowLeft, Pencil } from 'lucide-react'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { AttachmentUpload } from '@/features/files/index.ts'
import { customerSearchSchema } from '@/features/master-data/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import { useTRPC } from '@/trpc/react.ts'

export const Route = createFileRoute('/_authenticated/customer-management/customers/$customerId/')({
  component: CustomerDetailPage,
  head: () => ({ meta: [{ title: '客户档案详情 · Super Mega' }] }),
  validateSearch: customerSearchSchema
})

function CustomerDetailPage() {
  const { customerId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { currentSession } = Route.useRouteContext()
  const trpc = useTRPC()
  const id = Number(customerId)
  const validId = Number.isSafeInteger(id) && id > 0
  const query = useQuery({
    ...trpc.customer.list.queryOptions({ current: 1, pageSize: 1, id }),
    enabled: validId
  })
  const customer = query.data?.data[0]
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some((permission) => permission.startsWith('customer.') && !permission.endsWith('.read'))
  const goBack = () => void navigate({ to: '/customer-management/customers', search })

  return (
    <PageContainer breadcrumbRender={false} title={customer?.name ?? '客户档案详情'}>
      {validId && query.isPending && <Skeleton active />}
      {query.isError && (
        <Alert
          type="error"
          showIcon
          title={query.error.message}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      )}
      {(!validId || (query.isSuccess && !customer)) && <Alert type="warning" showIcon title="客户不存在或已删除" />}
      {customer && (
        <div className="space-y-4">
          <section className="border-border bg-surface overflow-hidden rounded-xl border shadow-xs">
            <div className="bg-surface-muted border-border flex flex-wrap items-start justify-between gap-4 border-b px-5 py-5 sm:px-6">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Tag color={customer.isDisable ? 'default' : 'success'}>{customer.isDisable ? '已停用' : '正常'}</Tag>
                  <span className="text-content-muted text-sm">客户档案</span>
                </div>
                <h2 className="text-content mt-3 mb-1 text-lg font-semibold break-words">{customer.name}</h2>
                <p className="text-content-muted m-0 text-sm">{customer.type === 'agent' ? '代理客户' : '普通客户'}</p>
              </div>
              <div className="border-border bg-surface rounded-lg border px-4 py-3 text-right">
                <span className="text-content-soft mb-1 block text-xs">客户 ID</span>
                <Typography.Text copyable={{ text: String(customer.id) }} className="font-mono" strong>
                  #{customer.id}
                </Typography.Text>
              </div>
            </div>
            <div className="space-y-6 p-5 sm:p-6">
              <section>
                <SectionHeading title="基本资料" />
                <Descriptions
                  column={{ xs: 1, sm: 2, lg: 3 }}
                  classNames={{ content: 'min-w-0 break-words whitespace-pre-wrap' }}
                  items={[
                    { key: 'salesperson', label: '业务员', children: customer.salesperson?.name || '—' },
                    { key: 'phone', label: '客户手机', children: customer.phone || '—' },
                    { key: 'email', label: '客户邮箱', children: customer.email || '—' },
                    {
                      key: 'wecomChatId',
                      label: '企微群 ID',
                      span: 'filled',
                      children: customer.wecomChatId ? (
                        <Typography.Text copyable>{customer.wecomChatId}</Typography.Text>
                      ) : (
                        '未关联'
                      )
                    },
                    { key: 'company', label: '企业信息', children: customer.companyInfo || '—', span: 'filled' },
                    {
                      key: 'tags',
                      label: '客户标签',
                      span: 'filled',
                      children: customer.tags.length ? (
                        <Space size={[4, 4]} wrap>
                          {customer.tags.map((tag) => (
                            <Tag key={tag.id} color={tag.color}>
                              {tag.name}
                            </Tag>
                          ))}
                        </Space>
                      ) : (
                        '—'
                      )
                    }
                  ]}
                />
              </section>
              <section>
                <SectionHeading title="账单设置" />
                <Authorized permissions={['customer.billing.update']}>
                  <Link to="/customer-management/customers/$customerId/collection-policy" params={{ customerId }}>
                    <Button className="mb-3">配置机器人自助出单</Button>
                  </Link>
                </Authorized>
                <Descriptions
                  column={{ xs: 1, sm: 2, lg: 3 }}
                  items={[
                    {
                      key: 'auto',
                      label: '自动生成收款账单组',
                      children: customer.autoCollectionBillGrouping ? '已开启' : '未开启'
                    },
                    {
                      key: 'day',
                      label: '每月归组日',
                      children: customer.collectionBillGroupingDay
                        ? `每月 ${customer.collectionBillGroupingDay} 日`
                        : '未设置'
                    },
                    {
                      key: 'autoSend',
                      label: '自动发送账单组',
                      children: customer.autoSendCollectionBillGroup ? '已开启（发送流程待接入）' : '未开启'
                    },
                    {
                      key: 'from',
                      label: '自动归组起始月份',
                      children: customer.collectionBillGroupingFrom || '—'
                    }
                  ]}
                />
              </section>
              <section>
                <SectionHeading title="档案记录" />
                <Descriptions
                  column={{ xs: 1, sm: 2, lg: 3 }}
                  items={[
                    {
                      key: 'creator',
                      label: '创建人',
                      children: (
                        <UserAvatarName avatar={customer.creator.avatar} name={customer.creator.name} size={28} />
                      )
                    },
                    {
                      key: 'created',
                      label: '创建时间',
                      children: dayjs(customer.createdAt).format('YYYY-MM-DD HH:mm:ss')
                    },
                    {
                      key: 'updated',
                      label: '最近更新',
                      children: dayjs(customer.updatedAt).format('YYYY-MM-DD HH:mm:ss')
                    }
                  ]}
                />
              </section>
            </div>
          </section>
          <section className="border-border bg-surface rounded-xl border p-5 shadow-xs sm:p-6">
            <SectionHeading title="附件与备注" />
            {customer.attachments.length > 0 ? (
              <AttachmentUpload
                disabled
                value={customer.attachments.map(({ file }) => file.id)}
                initialFiles={customer.attachments.map(({ file }) => ({
                  id: file.id,
                  name: file.originName || file.fileName,
                  fileType: file.fileType,
                  size: Number(file.fileSize)
                }))}
              />
            ) : (
              <p className="text-content-muted">暂无附件</p>
            )}
            <Descriptions
              className="mt-4"
              column={1}
              items={[
                {
                  key: 'note',
                  label: '备注',
                  children: <span className="wrap-break-word whitespace-pre-wrap">{customer.note || '—'}</span>
                }
              ]}
            />
          </section>
        </div>
      )}
      <div className="border-border bg-surface/95 sticky bottom-3 z-20 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 shadow-md backdrop-blur">
        <Button icon={<ArrowLeft aria-hidden size={16} />} onClick={goBack}>
          返回客户列表
        </Button>
        {customer && canManage && (
          <Authorized permissions={['customer.update']}>
            <Button
              type="primary"
              icon={<Pencil aria-hidden size={16} />}
              onClick={() =>
                void navigate({
                  to: '/customer-management/customers/$customerId/edit',
                  params: { customerId: String(customer.id) },
                  search: {},
                  state: { customerListReturnSearch: search }
                })
              }
            >
              编辑客户
            </Button>
          </Authorized>
        )}
      </div>
    </PageContainer>
  )
}

function SectionHeading({ title }: { title: string }) {
  return (
    <h2 className="text-content mb-4 flex items-center gap-2 text-base font-semibold">
      <span aria-hidden className="bg-accent size-2 rounded-full shadow-[0_0_0_0.25rem_var(--accent-soft)]" />
      {title}
    </h2>
  )
}
